import type {
  AgendamentoNaLista,
  EntradaAtualizarAgendamento,
  EntradaCriarAgendamento,
  EntradaReagendar,
  EnvelopeListagem,
  RespostaCalendario,
} from '@baiocchi/shared'

import { api, type ParametrosConsulta } from './cliente'

/**
 * `/agendamentos` (`03-endpoints-draft.md §7`, FL-09).
 *
 * Não existe "mover a data" por `PATCH`: mudar o horário é **reagendar**, que
 * preserva `reagendadoDe` e o rastro de "o cliente já desmarcou três vezes"
 * (`04-leads-workflows.md §1`). É por isso que o arrastar-e-soltar do calendário
 * chama `reagendar` e pede motivo, em vez de salvar em silêncio.
 */
export const agendamentosApi = {
  listar(filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<EnvelopeListagem<AgendamentoNaLista>>('/agendamentos', filtros, sinal)
  },

  /**
   * Grade da TELA-08, já agrupada por dia civil de São Paulo. Não é paginada — a
   * janela é limitada a 31 dias no servidor (`422 PERIODO_INVALIDO` acima disso).
   */
  calendario(filtros: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<RespostaCalendario>('/agendamentos/calendario', filtros, sinal)
  },

  detalhar(uuid: string) {
    return api.get<AgendamentoNaLista>(`/agendamentos/${uuid}`)
  },

  criar(entrada: EntradaCriarAgendamento) {
    return api.post<AgendamentoNaLista>('/agendamentos', entrada)
  },

  /**
   * `Record<string, unknown>` aqui desligava a única conferência de contrato que a web
   * tem: `schemaAtualizarAgendamento` é `.strict()` (aceita apenas `titulo`,
   * `observacao`, `terminaEm`, `diaInteiro` e `motivoId`), e com o tipo solto qualquer
   * campo a mais compilava e virava `422` em produção. Foi exatamente assim que o
   * `ativo` chegou às telas de unidade, equipe e catálogo.
   *
   * Sem chamador hoje — e é justamente por isso que valia trocar agora: o primeiro
   * uso nasce protegido, em vez de repetir o defeito.
   */
  atualizar(uuid: string, entrada: EntradaAtualizarAgendamento) {
    return api.patch<AgendamentoNaLista>(`/agendamentos/${uuid}`, entrada)
  },

  concluir(uuid: string, mensagem?: string) {
    return api.post<AgendamentoNaLista>(
      `/agendamentos/${uuid}/concluir`,
      mensagem ? { mensagem } : {},
    )
  },

  cancelar(uuid: string, motivo: string) {
    return api.post<AgendamentoNaLista>(`/agendamentos/${uuid}/cancelar`, { motivo })
  },

  naoCompareceu(uuid: string, motivo: string) {
    return api.post<AgendamentoNaLista>(`/agendamentos/${uuid}/nao-compareceu`, { motivo })
  },

  reagendar(uuid: string, entrada: EntradaReagendar) {
    return api.post<AgendamentoNaLista>(`/agendamentos/${uuid}/reagendar`, entrada)
  },
}
